/* eslint-disable no-alert */
import { ChangeEvent, useCallback, useEffect } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";

import { useInstance, useNetwork, useWebAuthn, useWebSocket } from "hooks";
import {
  IsSignInTermAndConditionEnabledState,
  userLoginCredRespState,
  SignInFormState,
  SignInScreenState,
  IsVerifiedUserState,
  SigninLoadingState,
} from "./states";
import { shallowCopy } from "utils";
import { CurrentScreenState, IFormInput } from "global-states";
import { ROUTES } from "constants/routes";
import { SIGNIN_FORM, SIGNIN_SCREENS } from "../constants";
import { API_URL } from "constants/api-url";
import GlobalInstanceManager from "instance-manger";
import { CurrentAuthCredState } from "./states";
import { ISignInCredType } from "./types";
import { ENVS } from "constants/common";
import { ERRORS, ErrorTypes } from "constants/errors";

let popupRef: Window | null = null;
/**
 * Custom hook for managing the sign-in process
 */

// eslint-disable-next-line @typescript-eslint/no-empty-function
let circularDependencyMethod: any = () => {};
export const useSignin = (additionalMethod?: any) => {
  const [loading, setLoading] = useRecoilState(SigninLoadingState);
  // State to manage whether the terms and conditions are checked
  const [checked, setChecked] = useRecoilState(
    IsSignInTermAndConditionEnabledState
  );
  const [isVerfiedUser, setIsVerfiedUser] = useRecoilState(IsVerifiedUserState);

  if (additionalMethod) {
    circularDependencyMethod = additionalMethod;
  }

  // State to set the current screen
  const setCurrentScreen = useSetRecoilState(CurrentScreenState);

  // Hook to get currentMethod details
  const { currentMethod, env } = useInstance();

  const { get: socialAuth } = useNetwork();

  // State to manage the current authentication method
  const [currentAuthMethod, setCurrentAuthMethod] =
    useRecoilState(CurrentAuthCredState);

  // State to manage the current step in the sign-in process
  const [signInCurrentScreen, setSignInInCurrentScreen] =
    useRecoilState(SignInScreenState);

  // State to manage the form data
  const [formState, setFormState] = useRecoilState(SignInFormState);

  const { get: getWebAuthn } = useWebAuthn();

  const { post } = useNetwork();

  const [userLoginCredResp, setUserLoginCredResp] = useRecoilState(
    userLoginCredRespState
  );

  //Hooks for websocket
  const { sendMessage, getEvent } = useWebSocket();

  const { post: submitCred, loading: submitCredLoading } = useNetwork();

  const { onErrorEvent, onStepChangeEvent, onSuccessEvent, webcomponentProps } =
    GlobalInstanceManager;

  const {
    instance_id,
    verified_user,
    auto_navigate,
    microsoft_redirect_url,
    facebook_redirect_url,
    apple_redirect_url,
    google_redirect_url,
  } = webcomponentProps;

  /**
   * Navigates to the sign-up page
   */
  const moveToSignupPage = useCallback(() => {
    GlobalInstanceManager.onStepChangeEvent({
      type: "change-page",
      pageTo: "signup",
      pageFrom: "singin",
      message: "user moved to signup page",
    });
    setCurrentScreen(ROUTES.SIGNUP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSSO = async (param: any) => {
    if (param.errorCode) {
      onErrorEvent({ ...param, type: "sso-data" });
      if (typeof popupRef?.close === "function") {
        try {
          popupRef.close();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log({ error });
        }

        if (param?.errorCode === "USER_NOT_FOUND" && auto_navigate) {
          circularDependencyMethod({
            email: param?.email ?? "",
            instanceId: instance_id,
          });
          setCurrentScreen(ROUTES.SIGNUP);
        } else {
          onErrorEvent({
            ...ERRORS[param.errorCode as keyof ErrorTypes],
            payload: { email: param?.email ?? "" },
          });
        }
      }
    } else if (Object.keys(param).length > 0 && param.email) {
      onStepChangeEvent({ ...param, type: "sso-data" });
      const currentForm = SIGNIN_FORM.find(
        (el) => el.name === "email"
      ) as IFormInput;
      if (param.type === "signUp") {
        setCurrentScreen(ROUTES.SIGNUP);
        circularDependencyMethod({}, param);
      } else {
        const newObj = shallowCopy(currentForm);
        if (newObj) {
          newObj.value = param.email;
          setFormState([newObj]);
          setSignInInCurrentScreen("methods");
          sendMessage("login", {
            userId: param.userId,
          });
          setUserLoginCredResp((prev) => ({ ...prev, userId: param.userId }));
        }
      }

      // Close the popup if the close function is available
      if (typeof popupRef?.close === "function") {
        try {
          popupRef.close();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log({ error });
        }
      }
    }
  };

  /**
   * useEffect hook to handle incoming messages and update form state accordingly.
   */
  useEffect(() => {
    // Message event listener function
    const messageListener = (event: MessageEvent) => {
      handleSSO(event.data);
    };

    // Add event listener for messages
    window.addEventListener("message", messageListener);

    // Cleanup function to remove event listener on component unmount
    return () => {
      window.removeEventListener("message", messageListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formState.length === 0 && currentMethod) {
      setCurrentAuthMethod(currentMethod);
      setFormState(SIGNIN_FORM.filter((el) => currentMethod === el.name));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMethod]);

  useEffect(() => {
    // Register an event listener for the "approvalStatus" event on the getEvent object
    (getEvent as any)?.on?.("approvalStatus", (resp: any) => {
      // eslint-disable-next-line no-console
      console.log("websocket respoinse == ", { resp });
    });

    (getEvent as any)?.on?.("loginCompleted", (resp: any) => {
      // eslint-disable-next-line no-console
      console.log("signinCompleted loginCompleted", { resp });
      onSuccessEvent(resp);
    });

    // Specify getEvent as a dependency for the useEffect hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getEvent]);

  const handleWebAuthn = useCallback(async () => {
    const resp: any = await post(API_URL.PASSKEY, {
      type: "authenticateOpts",
      payload: {
        rpId: window.location.origin,
        userId: userLoginCredResp.userId,
      },
    });

    if (resp.statusCode !== 200) {
      return onErrorEvent(resp);
    } else if (resp.authenticateOptions) {
      onStepChangeEvent({
        ...resp,
        type: "webAuthn",
        from: "login",
      });
      const authnResp: any = await getWebAuthn(resp.authenticateOptions);
      if (!authnResp.isError) {
        const verifyResp = await post(API_URL.PASSKEY, {
          type: "verifyAuthentication",
          payload: {
            authenticateOptResponse: {
              ...authnResp,
            },
            userId: userLoginCredResp.userId,
          },
        });
        // eslint-disable-next-line no-console
        console.log({ authnResp, verifyResp });
      }
    }
  }, [
    post,
    userLoginCredResp.userId,
    onErrorEvent,
    onStepChangeEvent,
    getWebAuthn,
  ]);

  /**
   * Placeholder function for submitting credentials
   */
  const onSubmitCred = async (
    authMethod?: "email" | "phone",
    formData?: IFormInput[]
  ) => {
    const method = authMethod || currentAuthMethod;
    const {
      countryCode,
      value,
      name = "",
    } = (formData || formState).find((el) => el.name === method) ?? {};
    if (!value || !name) {
      return;
    }

    const payload = {
      instanceId: instance_id,
      [name]:
        method === "phone" ? (value as string)?.replace(/\D/g, "") : value,
      countryCode: countryCode || undefined,
    };

    const resp = await submitCred(API_URL.LOGIN, payload);
    if (resp.statusCode !== 200) {
      onErrorEvent({ ...resp, payload });

      if (
        resp.message === "User not found" &&
        webcomponentProps.auto_navigate
      ) {
        await circularDependencyMethod?.(payload);
        moveToSignupPage();
      }
    } else {
      onStepChangeEvent({ ...resp, type: "signin-form" });
      sendMessage("login", {
        userId: resp.userId,
      });
      setUserLoginCredResp(resp);
      setSignInInCurrentScreen(SIGNIN_SCREENS.METHODS);
    }
    return { ...resp, payload };
  };

  /**
   * Placeholder function for submitting sign-in form
   */
  const onSubmitSignin = useCallback(() => {
    return "onSubmitSignin";
  }, []);

  /**
   * Handles change in form input fields
   * @param {ChangeEvent<HTMLInputElement>} event - The input change event
   */
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target ?? {};
      const { index, validation, formatter } = event as any;
      let inputVal = "";
      let validationInput = "";
      if (formatter) {
        const newData = formatter({
          value,
          countryCode: formState[index]?.countryCode,
        });
        inputVal = newData.formatted ?? value;
        validationInput = newData.validationInput ?? value;
      } else {
        inputVal = value;
        validationInput = value;
      }

      setFormState(() => {
        const newObj = shallowCopy(formState) ?? formState;
        if (newObj[index]) {
          newObj[index]["value"] = inputVal;
          if (validation) {
            const { isError, errorMessage } = validation(validationInput);
            newObj[index]["isError"] = isError;
            newObj[index]["errorMessage"] = errorMessage;
          }
        }

        return newObj;
      });
    },
    [formState, setFormState]
  );

  /**
   * Handles change in country code for phone number inputs
   * @param {string} value - The new country code
   * @param {number} index - The index of the form field to update
   */
  const handleChangeCountry = useCallback(
    (value: string, index: number) => {
      setFormState(() => {
        const newObj = shallowCopy(formState) ?? formState;
        if (newObj[index]) {
          newObj[index].countryCode = value;
        }

        return newObj;
      });
    },
    [formState, setFormState]
  );

  /**
   * Handles changing the authentication method
   * @param {string} key - The new authentication method key
   */
  const handleOnChangeMethod = (key: ISignInCredType) => {
    setCurrentAuthMethod(key);
    setFormState(SIGNIN_FORM.filter((el) => el.name === key));
  };

  const { post: loginViaLink } = useNetwork();

  const handleLoginViaLink = useCallback(async () => {
    const purpose =
      currentAuthMethod === "email"
        ? "emailLoginVerification"
        : "phoneLoginVerification";
    await loginViaLink(API_URL.APPROVAL, {
      purpose,
      userId: userLoginCredResp.userId,
    });
  }, [currentAuthMethod, loginViaLink, userLoginCredResp.userId]);

  /**
   * Handles option selection in the sign-in methods step
   * @param {any} e - The selected option
   */
  const handleAuthMethodOptions = useCallback(
    async (e: any) => {
      if (e.value === "biometric") {
        await handleWebAuthn();
      } else if (e.value === "otp") {
        setSignInInCurrentScreen(SIGNIN_SCREENS.APPROVAL);
        await handleLoginViaLink();
      } else {
        await handleLoginViaLink();
      }
    },
    [handleLoginViaLink, handleWebAuthn, setSignInInCurrentScreen]
  );

  const handleSocialSignUp = useCallback(
    async (el: { key: string }) => {
      const authType = auto_navigate ? "embeded" : "login";

      setLoading(true);
      let url = `${API_URL.OAUTH}/${
        el.key
      }?instanceId=${instance_id}&type=${authType}${
        env === ENVS.DEV ? "&test=true" : ""
      }&provider=${el.key}`;
      if (el.key === "facebook" && facebook_redirect_url) {
        url = url + `&redirect_url=${facebook_redirect_url}`;
      }
      if (el.key === "microsoft" && microsoft_redirect_url) {
        url = url + `&redirect_url=${microsoft_redirect_url}`;
      }

      if (el.key === "google" && google_redirect_url) {
        url = url + `&redirect_url=${google_redirect_url}`;
      }
      if (el.key === "apple" && apple_redirect_url) {
        url = url + `&redirect_url=${apple_redirect_url}`;
      }

      // &platform=${
      //   webcomponentProps.platform || "web"
      // }
      const resp = await socialAuth(url);
      if (resp?.statusCode === 200 && resp?.data?.url) {
        onStepChangeEvent({
          ...resp.data,
          type: "sso",
          from: "login",
          key: el.key,
        });
        popupRef = window.open(resp.data.url, "popup", "width=600,height=400");
      }
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      env,
      instance_id,
      socialAuth,
      microsoft_redirect_url,
      facebook_redirect_url,
      apple_redirect_url,
      google_redirect_url,
    ]
  );

  const handleCombinedInputChange = useCallback(
    (form: any) => {
      if (form.name === "phone") {
        setCurrentAuthMethod("phone");
      } else {
        setCurrentAuthMethod("email");
      }
      const index = SIGNIN_FORM.findIndex((el) => el.name === form.name);
      setFormState(() => {
        const newObj = shallowCopy(SIGNIN_FORM) ?? SIGNIN_FORM;
        if (newObj[index]) {
          newObj[index]["value"] = form.value;
          newObj[index]["isHidden"] = false;
          if (form.name === "phone") {
            newObj[index]["countryCode"] = form.countryCode;
          }
        }
        return newObj;
      });
    },
    [setCurrentAuthMethod, setFormState]
  );

  const exchangeWebCode = async (event: {
    code?: string;
    access_token?: string;
    state: any;
  }) => {
    const { code, access_token, state } = event;
    const params = new URLSearchParams();
    if (JSON.parse(state).provider)
      params.append("provider", JSON.parse(state).provider);
    //params.append('provider', 'facebook')
    if (code) params.append("code", code);
    if (access_token) params.append("access_token", access_token);
    if (state) params.append("state", state);
    const url = `${
      GlobalInstanceManager.webcomponentProps.apiUrl
    }/oauth?${params.toString()}`;
    window.location.href = url;
  };

  const manageDefaultUser = useCallback(async (user: any) => {
    try {
      if (Object.keys(user).length === 0 || !webcomponentProps.auto_navigate) {
        return;
      }

      setCurrentScreen("signin");
      const authMethod = user.email ? "email" : "phone";
      setCurrentAuthMethod(authMethod);
      const newObj = shallowCopy(SIGNIN_FORM);
      const payload = newObj
        .map((el: any) => {
          return {
            ...el,
            countryCode: user.countryCode || undefined,
            value: user[el.name] || "",
          };
        })
        .filter((el: any) => el.name === authMethod);

      setFormState(payload);
      await onSubmitCred(authMethod, payload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("ERROR:::", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOtherAccount = () => {
    setCurrentAuthMethod(currentMethod);
    setFormState(SIGNIN_FORM.filter((el) => currentMethod === el.name));
    setSignInInCurrentScreen(SIGNIN_SCREENS.SIGNIN);
    setIsVerfiedUser(false);
  };

  const handleVerifiedUser = async () => {
    setLoading(true);
    setSignInInCurrentScreen(SIGNIN_SCREENS.LOADING);
    const isEmailVerified = verified_user.email;
    const isPhoneVerified = verified_user.phone;
    if (isEmailVerified || isPhoneVerified) {
      const field: "email" | "phone" = isEmailVerified ? "email" : "phone";
      const value = isEmailVerified ? verified_user.email : verified_user.phone;

      const data = SIGNIN_FORM.filter((el) => el.name === field).map((el) => ({
        ...el,
        value,
        ...(field === "phone" && {
          countryCode: verified_user.countryCode ?? "+1",
        }),
      }));
      setCurrentAuthMethod(field);
      setFormState(data);
      const payload: {
        instanceId: string;
        phone?: string;
        email?: string;
        countryCode?: string;
      } = {
        instanceId: instance_id as string,
        [field]:
          field === "phone" ? (value as string)?.replace(/\D/g, "") : value,
        countryCode:
          field === "phone" ? verified_user.countryCode ?? "+1" : undefined,
      };

      const method = payload.email ? "email" : "phone";

      const payloadData = {
        ...payload,
        name: method,
        value: payload.email || payload.phone,
      };
      // eslint-disable-next-line no-console
      console.log({ payloadData });
      const resp = await onSubmitCred(method, [payloadData as any]);
      if (resp?.requestId && resp?.userId) {
        setIsVerfiedUser(true);
      } else {
        handleOtherAccount();
      }
      setLoading(false);
    }
  };
  /**
   * Appends object keys to the URL as query params and makes a GET request.
   *
   * @param {string} baseUrl - The base URL to send the GET request to.
   * @param {Object} params - Key-value pairs to append as query parameters.
   * @returns {Promise<any>} - The JSON response from the server.
   */
  async function exchangeCode(params: any) {
    const updatedParam = params;
    updatedParam.instanceId = updatedParam.instance_id || instance_id;
    const queryString = new URLSearchParams(updatedParam).toString();
    const urlWithParams = `${GlobalInstanceManager.webcomponentProps.apiUrl}/oauth?${queryString}`;
    const url = `/oauth?provider=google&access_token=${updatedParam.access_token}&code=${updatedParam.code}&redirect_url=com.googleusercontent.apps.718985979321-klp4ukpg1lch0m4acd4pe3dkmekr30hh%3A%2Foauth2redirect&instanceId=${instance_id}&type=embeded&responseType=json`;

    // eslint-disable-next-line no-console
    console.log("AVINSH wala Response == ", {
      queryString,
      urlWithParams,
      url,
    });
    try {
      const response = await socialAuth(url);
      // eslint-disable-next-line no-console
      console.log("AVINSH wala Response  hai yeah == ", { response });
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("exchangeToken failed:", error);
      throw error;
    }
  }

  return {
    onSubmitCred,
    onSubmitSignin,
    form: formState,
    checked,
    handleChangeCountry,
    handleChange,
    handleOnChangeMethod,
    moveToSignupPage,
    currentAuthMethod,
    setChecked,
    handleWebAuthn,
    handleLoginViaLink,
    handleAuthMethodOptions,
    signInCurrentScreen,
    setSignInInCurrentScreen,
    handleSocialSignUp,
    submitCredLoading,
    handleCombinedInputChange,
    manageDefaultUser,
    loading,
    handleOtherAccount,
    verified_user,
    isVerfiedUser,
    handleVerifiedUser,
    handleSignInSSO: handleSSO,
    exchangeCode,
    exchangeWebCode,
  };
};
